import React, { useEffect, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import styled from "@emotion/styled";
import Translate from "@src/components/translation/Translate";
import { mediaQueries } from "@src/styles/theme";
import Container from "@src/components/Container";
import { theme } from "@src/components/nessie-web";
import VideoThumbnail from "@src/assets/images/dojo-islands/islands-thumbnail.png";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";
import cookies from "cookies-js";
import { cookiesWork } from "@src/utils/cookies";

const {
  colors: { dt_taro20, dt_taro90, dt_white },
} = theme;

const HeroSectionContainer = styled.section`
  width: 100%;
  background-color: ${dt_taro20};
  overflow: unset;
`;

const HeroBackgroundContainer = styled.div<{ bgImage: string }>`
  background: url("${(props) => props.bgImage}");
  background-color: #e1f8ff;
  background-size: cover;
  background-position: top center;
  background-repeat: no-repeat;
  width: 100%;
  position: relative;
  display: flex;
  min-height: 767.23px;
  padding-bottom: 250px;
  ${mediaQueries[0]} {
    min-height: 1143.29px;
    padding-bottom: 300px;
  }
`;

const HeroContainer = styled(Container)`
  z-index: 2;
  text-align: center;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const HeroContent = styled.div`
  width: auto;
  ${mediaQueries[0]} {
    padding-bottom: 0px;
    width: 83.33%;
    margin-top: 84px;
  }
`;

const HeroTitle = styled.div`
  font-weight: 800;
  font-size: 24px;
  line-height: 30px;
  letter-spacing: -0.25px;
  text-align: center;
  text-shadow: 0px 0px 54px #000000;
  color: ${dt_white};
  ${mediaQueries[0]} {
    letter-spacing: -0.5px;
    font-size: 50px;
    line-height: 50px;
  }
`;

const VideoContainer = styled.div`
  border: 6px solid ${dt_taro90};
  background-color: ${dt_taro90};
  background-image: url(${VideoThumbnail});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  border-radius: 24px;
  width: 70%;
  margin-top: 24px;
  ${mediaQueries[0]} {
    margin-top: 54px;
    border: 9px solid ${dt_taro90};
  }
  position: relative;
  overflow: hidden;
  .inner {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='162' height='154' viewBox='0 0 162 154' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M19.1732 101.252C10.3486 75.3522 14.9192 44.1838 39.9231 28.1505C65.2463 11.9124 97.5676 19.3274 120.762 36.1098C143.607 52.6397 158.349 79.674 146.791 105.476C135.88 129.831 104.314 136.09 76.3858 135.166C50.875 134.322 26.7635 123.529 19.1732 101.252Z' fill='%2300B2F7' /%3E%3Cpath d='M105.835 72.8636C109.169 74.7881 109.169 79.5993 105.835 81.5238L72.4446 100.802C69.1112 102.727 64.9446 100.321 64.9446 96.4719L64.9446 57.9155C64.9446 54.0665 69.1112 51.6608 72.4446 53.5853L105.835 72.8636Z' fill='white' /%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    cursor: pointer;
    background-size: 91.65px 99.77px;
    appearance: none;
    outline: none;
    border: none;
    ${mediaQueries[0]} {
      background-size: 162px 154px;
    }
  }
`;

const VideoFrame = styled.div`
  height: 191.96px;
  ${mediaQueries[0]} {
    height: 426.79px;
  }
`;

const VideoText = styled.div`
  text-align: right;
  position: relative;
  margin-top: 18px;
  .img-text {
    width: 133.16px;
    height: 41.19px;
  }
  .img-arrow {
    width: 26.18px;
    height: 29.46px;
    position: absolute;
    right: -20px;
    top: 14px;
    transform: rotate(85deg);
  }
  .break {
    display: none;
  }
  ${mediaQueries[0]} {
    margin-top: 0px;
    position: absolute;
    left: -50px;
    bottom: 20%;
    .img-text {
      width: 181.25px;
      height: 57.59px;
    }
    .img-arrow {
      width: 52px;
      height: 44px;
      display: inline-block;
      position: relative;
      right: unset;
      top: unset;
      transform: rotate(0deg);
    }
    .break {
      display: block;
    }
  }
`;

const AccentCurve = styled.div`
  content: "";
  height: 93px;
  width: 100%;
  background-image: url("data:image/svg+xml,%3Csvg preserveAspectRatio='none' viewBox='0 0 1441 93' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.0366211 42.8277C377.844 12.9166 1065.82 -47.5623 1440.04 67.426V92.4039L0.0366211 92.4039L0.0366211 42.8277Z' fill='%23E1F8FF'/%3E%3C/svg%3E%0A");
  display: block;
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  background-repeat: no-repeat;
  background-size: 102% 100%;
  z-index: 3;
`;

const AccentImage = styled.img`
  position: absolute;
  &.accent-blocks {
    bottom: 0px;
    z-index: 20;
    right: 0;
    transform: rotate(3.37deg);
    width: 147.62px;
    height: 103.7px;
    ${mediaQueries[0]} {
      bottom: -18px;
      width: 326.15px;
      height: 229.12px;
    }
  }
  &.accent-crane {
    left: -30px;
    bottom: -5px;
    z-index: 2;
    width: 135.16px;
    height: 140.43px;
    ${mediaQueries[0]} {
      width: 407.26px;
      height: 423.14px;
      left: 0;
      bottom: -30px;
    }
  }
`;

const StyledSign = styled.div`
  position: absolute;
  width: 480.61px;
  height: 302.28px;
  background-image: url("https://static.classdojo.com/img/page_dojo-islands/islands-sign.png");
  background-repeat: no-repeat;
  background-size: 100% 100%;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%) scale(0.6);
  padding-top: 122px;
  padding-left: 50px;
  padding-right: 50px;
  text-align: center;
  z-index: 3;
  ${mediaQueries[0]} {
    bottom: 0;
    transform: translateX(-50%);
  }
`;

const SignHeading = styled.div`
  font-weight: 800;
  font-size: 16px;
  line-height: 14px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
`;

const SignText = styled.div`
  font-weight: 600;
  font-size: 18px;
  line-height: 21px;
`;

type HeroSectionProps = {
  hero_text: string;
  sign_title: string;
  sign_text: string;
  monsterIslandLogo: string;
  heroVideoId: string;
};

const DojoIslandsHero: React.FC<HeroSectionProps> = ({
  hero_text,
  sign_title,
  sign_text,
  monsterIslandLogo,
  heroVideoId,
}) => {
  const modalContext = React.useContext(ModalContext);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  function openVideoModal() {
    modalContext.showModal(ModalType.VideoModal, { youtubeID: heroVideoId });
  }

  return (
    <HeroSectionContainer>
      <HeroBackgroundContainer
        bgImage="https://static.classdojo.com/img/page_dojo-islands/islands-hero-bg.jpg"
        style={{ backgroundPosition: `${scrollPosition / 11 - 200}px center` }}
      >
        <HeroContainer>
          <HeroContent>
            <img alt="Dojo islands logo" src={monsterIslandLogo} width="330px"></img>
            <HeroTitle>
              <Translate path={hero_text} />
            </HeroTitle>
          </HeroContent>
          <VideoContainer>
            <VideoFrame>
              <button className="inner" onClick={openVideoModal}></button>
            </VideoFrame>
          </VideoContainer>
        </HeroContainer>
        <StyledSign>
          <SignHeading>
            <Translate path={sign_title} />
          </SignHeading>
          <SignText>
            <Translate path={sign_text} />
          </SignText>
        </StyledSign>
        <AccentImage
          alt=""
          className="accent-blocks"
          src="https://static.classdojo.com/img/page_dojo-islands/islands-blocks.png"
          width="326px"
        />
        <AccentImage
          alt=""
          className="accent-crane"
          src="https://static.classdojo.com/img/page_dojo-islands/islands-crane.png"
          width="407.26px"
        />
        <AccentCurve />
      </HeroBackgroundContainer>
    </HeroSectionContainer>
  );
};

export default DojoIslandsHero;
